import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProjectSection } from '../../Services/UserProjectService';

interface ProgressBarProps {
  sections: ProjectSection[];
  currentSectionIndex: number;
  currentContentIndex: number;
  navigationDirection: 'forward' | 'backward' | undefined;
  onHoverChange?: (isHovered: boolean) => void;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  sections,
  currentSectionIndex,
  currentContentIndex,
  navigationDirection,
  onHoverChange
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  // Helper function to determine if a content position is before or at current position
  const isContentBeforeOrAtCurrent = (sectionIdx: number, contentIdx: number): boolean => {
    if (sectionIdx < currentSectionIndex) {
      return true; // All content in previous sections
    } else if (sectionIdx === currentSectionIndex) {
      return contentIdx <= currentContentIndex; // Content in current section up to and including current
    } else {
      return false; // Content in future sections
    }
  };

  // Find the furthest uncompleted content position
  const getFurthestUncompletedPosition = (): { sectionIndex: number; contentIndex: number } | null => {
    for (let sectionIdx = 0; sectionIdx < sections.length; sectionIdx++) {
      const section = sections[sectionIdx];
      if (section.contents) {
        for (let contentIdx = 0; contentIdx < section.contents.length; contentIdx++) {
          const content = section.contents[contentIdx];
          if (!content.isCompleted) {
            return { sectionIndex: sectionIdx, contentIndex: contentIdx };
          }
        }
      }
    }
    return null; // All content is completed
  };

  // Check if current position is at the furthest uncompleted content
  const isAtFurthestUncompletedPosition = (): boolean => {
    const furthest = getFurthestUncompletedPosition();
    if (!furthest) return true; // If all completed, current position is valid
    return furthest.sectionIndex === currentSectionIndex && furthest.contentIndex === currentContentIndex;
  };

  const handleHoverChange = (hovered: boolean) => {
    setIsHovered(hovered);
    if (onHoverChange) {
      onHoverChange(hovered);
    }
  };
  // Calculate total contents across all sections
  const totalContents = sections.reduce((total, section) => total + (section.contents?.length || 0), 0);
  
  // Fixed pixel gap between sections
  const gapPixels = 4; // 4px gap between sections
  
  // Generate progress segments for each section
  const progressSegments = sections.map((section, sectionIndex) => {
    const sectionContentCount = section.contents?.length || 0;
    // Simple proportional width without considering gaps (gaps handled by CSS)
    const sectionWidth = (sectionContentCount / totalContents) * 100;

    // Calculate how many contents in this section are completed
    let completedInSection = 0;
    let completedBeforeCurrentInSection = 0;
    let currentInSection = false;

    if (sectionIndex < currentSectionIndex) {
      // All contents in previous sections are completed and before current
      completedInSection = sectionContentCount;
      completedBeforeCurrentInSection = sectionContentCount;
    } else if (sectionIndex === currentSectionIndex) {
      // Current section: count actual completed content
      if (section.contents) {
        for (let contentIdx = 0; contentIdx < section.contents.length; contentIdx++) {
          const content = section.contents[contentIdx];
          if (content.isCompleted) {
            completedInSection++;
            // Check if this completed content is before or at current position
            if (contentIdx <= currentContentIndex) {
              completedBeforeCurrentInSection++;
            }
          }
        }
      }
      currentInSection = true;
    } else {
      // Future sections: count completed content (all will be "after current")
      if (section.contents) {
        completedInSection = section.contents.filter(content => content.isCompleted).length;
      }
      completedBeforeCurrentInSection = 0; // All completed content in future sections is "after current"
    }

    const completedWidth = sectionContentCount > 0 ? (completedInSection / sectionContentCount) * sectionWidth : 0;
    const completedBeforeCurrentWidth = sectionContentCount > 0 ? (completedBeforeCurrentInSection / sectionContentCount) * sectionWidth : 0;
    const currentWidth = currentInSection && sectionContentCount > 0 ? (1 / sectionContentCount) * sectionWidth : 0;

    return {
      sectionIndex,
      sectionWidth,
      completedWidth,
      completedBeforeCurrentWidth,
      completedAfterCurrentWidth: completedWidth - completedBeforeCurrentWidth,
      currentWidth,
      isCurrentSection: currentInSection
    };
  });

  return (
    <div className="progress-bar-container">
      <div style={{ display: 'flex', alignItems: 'center', position: 'relative', justifyContent: 'space-between', padding: '0 3rem' }}>
        {/* Home icon */}
        <div 
          onClick={() => navigate('/user/dashboard')}
          style={{
            cursor: 'pointer',
            padding: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <svg 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="#000" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
        </div>

        {/* Progress bar with transition */}
        <div 
          className="progress-bar-track" 
          style={{ 
            flex: '1',
            maxWidth: '85%',
            margin: '0 20px',
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.3s ease',
            visibility: isHovered ? 'visible' : 'hidden'
          }}
        >
          {progressSegments.map((segment, index) => (
            <div
              key={segment.sectionIndex}
              className="progress-section"
              style={{ 
                flex: segment.sectionWidth, // Use flex-grow for proportional sizing
                marginRight: index < progressSegments.length - 1 ? `${gapPixels}px` : 0
              }}
            >
              {/* Completed portion BEFORE or AT current position (BLACK) */}
              {segment.completedBeforeCurrentWidth > 0 && (
                <div
                  className="progress-completed progress-black"
                  style={{ width: `${(segment.completedBeforeCurrentWidth / segment.sectionWidth) * 100}%` }}
                />
              )}

              {/* Completed portion AFTER current position (GRAY) */}
              {segment.completedAfterCurrentWidth > 0 && (
                <div
                  className="progress-completed progress-grey"
                  style={{
                    width: `${(segment.completedAfterCurrentWidth / segment.sectionWidth) * 100}%`,
                    left: `${(segment.completedBeforeCurrentWidth / segment.sectionWidth) * 100}%`
                  }}
                />
              )}

              {/* Current content indicator */}
              {segment.currentWidth > 0 && (
                <div
                  className={`progress-current ${!isAtFurthestUncompletedPosition() ? 'progress-current-grey' : ''}`}
                  style={{
                    width: `${(segment.currentWidth / segment.sectionWidth) * 100}%`,
                    left: `${(segment.completedBeforeCurrentWidth / segment.sectionWidth) * 100}%`
                  }}
                />
              )}

            </div>
          ))}
        </div>
        
        {/* Clock icon */}
        <div 
          onMouseEnter={() => handleHoverChange(true)}
          onMouseLeave={() => handleHoverChange(false)}
          style={{
            cursor: 'pointer',
            padding: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <svg 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="#000" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
        </div>
      </div>
      
      {/* Copyright message */}
      <div className="copyright-message text-center" style={{
        fontSize: '0.95rem',
        color: '#000',
        marginTop: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        © {new Date().getFullYear()} Plano Invisible. Todos los derechos reservados.
      </div>
    </div>
  );
};

export default ProgressBar;